import * as React from 'react';
import { FileText } from 'lucide-react';
import {
  Sheet,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceNotice,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';
import { ApiError } from '@/lib/api/client';
import {
  useIntakeRunsQuery,
  useWithdrawDocumentMutation,
  type IntakeDocument,
  type IntakeRun,
} from '@/lib/api/requirement-library-intake';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import {
  formatBytes,
  formatCost,
  formatWhen,
  pageCountWords,
  runCountsLine,
  runStatusLabel,
  runStatusTone,
  type RunTone,
} from './intake-logic';

/**
 * "Document imports": every AI document intake run, newest first (T-0154).
 *
 * A run in progress is discoverable here after its sheet was closed, and
 * any run can be reopened: one in review to carry on, a closed one to see
 * what was decided. A source document uploaded by mistake can be
 * withdrawn by a firm admin. Withdraw is offered only when the server says
 * the caller may (`can_withdraw_sources`, T-0157); the server refuses
 * anyone else regardless.
 */

export interface IntakeRunsSheetProps {
  onClose: () => void;
  /** Open one run in the intake sheet. */
  onOpenRun: (runId: string) => void;
  /** Start a new import. */
  onImport: () => void;
}

const TONE: Record<RunTone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  ai: 'border border-ai-surface-border bg-ai-surface text-ai-ink',
  'at-risk': 'bg-posture-at-risk-tint text-posture-at-risk',
  critical: 'bg-posture-critical-tint text-posture-critical',
  healthy: 'bg-posture-healthy-tint text-posture-healthy',
};

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function IntakeRunsSheet({ onClose, onOpenRun, onImport }: IntakeRunsSheetProps) {
  const runsQuery = useIntakeRunsQuery(true);
  const runs = React.useMemo(() => runsQuery.data?.runs ?? [], [runsQuery.data]);
  const canWithdraw = runsQuery.data?.can_withdraw_sources === true;
  const userIds = React.useMemo(
    () => Array.from(new Set(runs.flatMap((r) => (r.started_by ? [r.started_by] : [])))),
    [runs],
  );
  const names = useUsersLookupQuery(userIds).data?.items;
  const nameOf = (id: string | null | undefined) =>
    id ? (names?.find((u) => u.id === id)?.display_name ?? 'A curator') : 'Unknown';

  const withdrawMutation = useWithdrawDocumentMutation();
  const [withdrawing, setWithdrawing] = React.useState<IntakeDocument | null>(null);
  const [withdrawError, setWithdrawError] = React.useState<string | null>(null);

  const withdraw = () => {
    if (!withdrawing) return;
    setWithdrawError(null);
    withdrawMutation.mutate(withdrawing.id, {
      onSuccess: () => setWithdrawing(null),
      onError: (err) =>
        setWithdrawError(
          err instanceof ApiError && err.code === 'authorization'
            ? 'Only a firm admin can withdraw a source document.'
            : messageOf(err, 'The document could not be withdrawn.'),
        ),
    });
  };

  const openLabel = (run: IntakeRun) =>
    run.status === 'ready_for_review' && run.outcome !== 'none_found' ? 'Review' : 'Open';

  return (
    <>
      <Sheet
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title="Document imports"
        description="Documents read by AI for the requirement library, newest first. Source documents are kept so citations keep working."
        actions={
          <>
            <WorkspaceButton size="md" onClick={onClose}>
              Close
            </WorkspaceButton>
            <WorkspaceButton size="md" variant="primary" onClick={onImport}>
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              Import from a document
            </WorkspaceButton>
          </>
        }
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto bg-paper-50 p-5">
          {runsQuery.isPending ? (
            <div role="status" className="flex items-center gap-2 text-body-sm text-ink-500">
              <Spinner /> Loading document imports…
            </div>
          ) : runsQuery.isError ? (
            <WorkspaceNotice tone="critical">
              The document imports could not be loaded. {messageOf(runsQuery.error, '')}{' '}
              <button
                type="button"
                className="font-medium underline underline-offset-[3px]"
                onClick={() => void runsQuery.refetch()}
              >
                Try again
              </button>
            </WorkspaceNotice>
          ) : runs.length === 0 ? (
            <div className="rounded-card border border-ink-100 bg-paper-0 px-6 py-14 text-center text-body-sm text-ink-500">
              <strong className="mb-1 block text-h3 font-semibold text-ink-900">
                No documents have been imported yet
              </strong>
              Import an RFP, a requirements appendix or a client list to grow the library from it.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-card border border-ink-100 bg-paper-0">
              <table className="w-full border-collapse text-left text-body-sm">
                <caption className="sr-only">Document imports</caption>
                <thead>
                  <tr className="border-b border-ink-100 text-meta font-medium text-ink-500">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Document
                    </th>
                    <th scope="col" className="px-2 py-2 font-medium">
                      Started by
                    </th>
                    <th scope="col" className="px-2 py-2 font-medium">
                      When
                    </th>
                    <th scope="col" className="px-2 py-2 font-medium">
                      Status
                    </th>
                    <th scope="col" className="px-2 py-2 font-medium">
                      Result
                    </th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">
                      Cost
                    </th>
                    <th scope="col" className="px-4 py-2">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr
                      key={run.id}
                      data-testid={`intake-run-${run.id}`}
                      className="border-b border-ink-100 align-top last:border-b-0"
                    >
                      <td className="max-w-[320px] px-4 py-2.5">
                        <span className="block truncate font-medium text-ink-900">
                          {run.document.file_name}
                        </span>
                        <span className="text-meta text-ink-500">
                          {[
                            pageCountWords(run.document.page_count, run.document.page_source),
                            formatBytes(run.document.byte_size) || null,
                            run.previous_run_id ? 'read again' : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        {run.document.withdrawn ? (
                          <span className="ml-1.5 inline-flex h-5 items-center rounded-full bg-posture-critical-tint px-2 text-[11px] font-medium text-posture-critical">
                            Source withdrawn
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-ink-700">
                        {nameOf(run.started_by)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-ink-700">
                        {formatWhen(run.created_at)}
                      </td>
                      <td className="px-2 py-2.5">
                        <span
                          className={cn(
                            'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-meta font-medium',
                            TONE[runStatusTone(run)],
                          )}
                        >
                          {runStatusLabel(run)}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-ink-700">{runCountsLine(run)}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-right font-mono text-meta text-ink-700">
                        {formatCost(run.cost_cents) || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <span className="inline-flex gap-1.5">
                          <WorkspaceButton
                            aria-label={`${openLabel(run)} the import of ${run.document.file_name}`}
                            onClick={() => onOpenRun(run.id)}
                          >
                            {openLabel(run)}
                          </WorkspaceButton>
                          {canWithdraw && !run.document.withdrawn ? (
                            <WorkspaceButton
                              variant="tertiary"
                              title="Firm admins only"
                              aria-label={`Withdraw ${run.document.file_name}`}
                              onClick={() => {
                                setWithdrawError(null);
                                setWithdrawing(run.document);
                              }}
                            >
                              Withdraw
                            </WorkspaceButton>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Sheet>

      <WorkspaceDialog
        open={withdrawing != null}
        onOpenChange={(next) => {
          if (!next) setWithdrawing(null);
        }}
        title={`Withdraw ${withdrawing?.file_name ?? 'this document'}?`}
        description="For a document uploaded by mistake. Firm admins only. This cannot be undone."
        actions={
          <>
            <WorkspaceButton size="md" onClick={() => setWithdrawing(null)}>
              Keep the document
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="destructive"
              disabled={withdrawMutation.isPending}
              onClick={withdraw}
            >
              {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw the document'}
            </WorkspaceButton>
          </>
        }
      >
        <p className="text-body-sm text-ink-700">
          The document can no longer be opened, downloaded or read again, and an import of it that
          is still in review can no longer be confirmed.
        </p>
        <p className="text-body-sm text-ink-700">
          Nothing is deleted. Requirements that cite it stay in the library and keep the quoted text
          of their citation; their source shows as withdrawn.
        </p>
        {withdrawError ? <WorkspaceNotice tone="critical">{withdrawError}</WorkspaceNotice> : null}
      </WorkspaceDialog>
    </>
  );
}
